import {
  useCallback, useEffect, useRef, useState
} from "react";
import {
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import {
  getApiErrorMessage,
  whatsappService
} from "../services/api";
import type {
  VerificationCodeResponse
} from "../services/api";
import {
  OnboardingEvents,
  trackEvent
} from '../analytics/posthog';
import {
  copyTextToClipboard
} from '../utils/clipboard';
import {
  useCountdown
} from "../hooks/useCountdown";
import {
  useAuth
} from "../hooks/useAuth";
import {
  usePairingSession
} from "../hooks/usePairingSession";
import {
  ChannelModalShell
} from "./pairing/ChannelModalShell";

interface WhatsAppPairingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAttemptStarted?: (expiresAt: string) => void;
  onSuccess: () => void;
}

const PAIRING_SESSION_KEY_PREFIX = "catch.pairing.whatsapp:";

/**
 * A generate-code response as read back from sessionStorage. One stored before
 * the backend returned `whatsapp_number` (GET-16141) has no number, and must
 * not be re-fetched to fill it in: the frontend can ship before the backend,
 * and that re-fetch would then loop.
 */
type StoredVerificationCode = Omit<VerificationCodeResponse, "whatsapp_number">
  & Partial<Pick<VerificationCodeResponse, "whatsapp_number">>;

type CopyTarget = "code" | "link" | "number";

/** idle → tried (Open WhatsApp clicked) → manual ("Didn't work?" clicked) */
type LinkStage = "idle" | "tried" | "manual";

interface CopyableRowProps {
  label: string;
  value: string;
  copyLabel: string;
  copied: boolean;
  onCopy: (value: string) => void;
}

function CopyableRow({
  label,
  value,
  copyLabel,
  copied,
  onCopy,
}: CopyableRowProps) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="text-left">
        <dt className="text-xs text-gray-500">{label}</dt>
        <dd className="text-lg font-mono text-gray-900">{value}</dd>
      </div>
      <button
        type="button"
        onClick={() => onCopy(value)}
        className="ml-2 p-2 text-gray-400 hover:text-gray-600"
        aria-label={copyLabel}
        title={copyLabel}
      >
        {copied ? (
          <CheckIcon className="h-5 w-5 text-green-600" />
        ) : (
          <ClipboardDocumentIcon className="h-5 w-5" />
        )}
      </button>
    </div>
  );
}

export function WhatsAppPairingModal({
  isOpen,
  onClose,
  onAttemptStarted,
  onSuccess,
}: WhatsAppPairingModalProps) {
  const { user } = useAuth();
  const {
    data: verificationData,
    saveCode,
    clearAll: clearPairingSession,
  } = usePairingSession<StoredVerificationCode>(
    `${PAIRING_SESSION_KEY_PREFIX}${user?.id ?? "anonymous"}`,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<CopyTarget | null>(null);
  // The deep link carries the number and the message, so it leads; the manual
  // path is one "Didn't work?" away once the user has tried it. Survives a
  // close, since users close the modal to go and try WhatsApp.
  const [linkStage, setLinkStage] = useState<LinkStage>("idle");
  const modalOpenTracked = useRef(false);
  const successHandled = useRef(false);
  const codeGenerationInFlight = useRef(false);
  const isOpenRef = useRef(isOpen);
  const onCloseRef = useRef(onClose);
  const onAttemptStartedRef = useRef(onAttemptStarted);
  const onSuccessRef = useRef(onSuccess);
  isOpenRef.current = isOpen;
  onCloseRef.current = onClose;
  onAttemptStartedRef.current = onAttemptStarted;
  onSuccessRef.current = onSuccess;

  const {
    formatted: countdownFormatted,
    reset: resetCountdown,
    timeLeft,
  } = useCountdown(verificationData?.expires_at);

  const resetTransientState = useCallback(() => {
    setError(null);
    setCopied(null);
  }, []);

  const clearPairing = useCallback(() => {
    clearPairingSession();
    resetTransientState();
    resetCountdown();
    setLinkStage("idle");
  }, [clearPairingSession, resetCountdown, resetTransientState]);

  const handleSuccess = useCallback(() => {
    if (successHandled.current) return;

    successHandled.current = true;
    trackEvent(OnboardingEvents.WHATSAPP_CONNECTED);
    clearPairing();
    onCloseRef.current();
    onSuccessRef.current();
  }, [clearPairing]);

  const completeIfConnected = useCallback(async (): Promise<boolean> => {
    try {
      const status = await whatsappService.getStatus();
      if (status.connected) {
        if (isOpenRef.current) {
          handleSuccess();
        }
        return true;
      }
    } catch {
      // A status failure should not prevent generating or using a pairing code.
    }
    return false;
  }, [handleSuccess]);

  const generateVerificationCode = useCallback(async () => {
    if (codeGenerationInFlight.current) return;

    codeGenerationInFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      // Reopening the modal after WhatsApp finished in the background should
      // advance onboarding instead of asking the backend for an invalid new code.
      if (await completeIfConnected()) return;
      if (!isOpenRef.current) return;

      try {
        const data = await whatsappService.generateVerificationCode();
        if (!isOpenRef.current) return;

        saveCode(data);
        onAttemptStartedRef.current?.(data.expires_at);
        trackEvent(OnboardingEvents.WHATSAPP_CODE_GENERATED);
      } catch (err: unknown) {
        if (!isOpenRef.current) return;

        // Pairing can complete between the status check and code generation.
        // Confirm the authoritative state before showing or tracking a failure.
        if (await completeIfConnected()) return;
        if (!isOpenRef.current) return;

        const errorMessage = getApiErrorMessage(
          err,
          "Failed to generate verification code",
        );
        trackEvent(OnboardingEvents.WHATSAPP_CONNECTION_FAILED, {
          error: errorMessage,
        });
        setError(errorMessage);
      }
    } finally {
      codeGenerationInFlight.current = false;
      setLoading(false);
    }
  }, [completeIfConnected, saveCode]);

  useEffect(() => {
    if (isOpen && !modalOpenTracked.current) {
      successHandled.current = false;
      trackEvent(OnboardingEvents.WHATSAPP_MODAL_OPENED);
      modalOpenTracked.current = true;
    }
    if (!isOpen) {
      modalOpenTracked.current = false;
    }
  }, [isOpen]);

  // An expired code left on screen still has a working copy button and
  // "Open WhatsApp" link, so users sent it and were told it was invalid
  // (GET-14758). Replace it instead. `timeLeft` starts at 0 before the countdown
  // reads `expires_at`, so the timestamp itself decides.
  const codeExpired =
    verificationData !== null &&
    verificationData !== undefined &&
    timeLeft <= 0 &&
    Date.parse(verificationData.expires_at) <= Date.now();

  // Re-armed only once a code reads as live. A browser clock running ahead of
  // the server reads every fresh code as expired, so it never re-arms and gets
  // one replacement rather than a loop; the "Generate a new one" link remains.
  const autoReplaceArmed = useRef(true);
  if (verificationData && !codeExpired) autoReplaceArmed.current = true;

  useEffect(() => {
    // A generation error only retries after the user explicitly clicks "Try again".
    if (!isOpen || loading || error) return;
    if (!verificationData) {
      void generateVerificationCode();
    } else if (codeExpired && autoReplaceArmed.current) {
      autoReplaceArmed.current = false;
      void generateVerificationCode();
    }
  }, [codeExpired, error, generateVerificationCode, isOpen, loading, verificationData]);

  const handleRegenerateCode = () => {
    trackEvent(OnboardingEvents.WHATSAPP_CODE_REGENERATE_CLICKED);
    void generateVerificationCode();
  };

  const copyToClipboard = async (text: string, type: CopyTarget) => {
    try {
      await copyTextToClipboard(text);
      setCopied(type);
      trackEvent(OnboardingEvents.WHATSAPP_CODE_COPIED, { type });
      setTimeout(
        () => setCopied((current) => (current === type ? null : current)),
        2000,
      );
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const handleOpenWhatsApp = () => {
    trackEvent(OnboardingEvents.WHATSAPP_LINK_CLICKED);
    // Trying the link again must not fold away the manual details.
    setLinkStage((stage) => (stage === "manual" ? stage : "tried"));
  };

  const handleShowManualOptions = () => {
    trackEvent(OnboardingEvents.WHATSAPP_MANUAL_OPTIONS_OPENED);
    setLinkStage("manual");
  };

  const handleClose = () => {
    trackEvent(OnboardingEvents.WHATSAPP_MODAL_CLOSED);
    // Keep the still-valid code in the session so the onboarding step can
    // offer a real resume path after the user returns from WhatsApp.
    resetTransientState();
    onClose();
  };

  // Check for successful connection every 5 seconds
  // Use a ref to track timeLeft so the polling interval isn't recreated every second
  const timeLeftRef = useRef(timeLeft);
  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  useEffect(() => {
    if (!isOpen || !verificationData) return;

    const checkConnection = async () => {
      // Check timeLeft via ref to avoid recreating interval every second
      if (timeLeftRef.current <= 0) return;

      await completeIfConnected();
    };

    let foregroundCheckTimeout: ReturnType<typeof setTimeout> | undefined;
    const scheduleForegroundCheck = () => {
      if (foregroundCheckTimeout) clearTimeout(foregroundCheckTimeout);
      foregroundCheckTimeout = setTimeout(() => {
        // Foreground recovery intentionally ignores timeLeft: pairing may have
        // completed while the browser was backgrounded, just as the code expired.
        void completeIfConnected();
      }, 100);
    };

    const checkConnectionOnVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleForegroundCheck();
      }
    };

    // Check immediately when verification data is ready
    void checkConnection();

    const interval = setInterval(() => void checkConnection(), 5000);
    window.addEventListener("focus", scheduleForegroundCheck);
    document.addEventListener(
      "visibilitychange",
      checkConnectionOnVisibilityChange,
    );
    return () => {
      clearInterval(interval);
      if (foregroundCheckTimeout) clearTimeout(foregroundCheckTimeout);
      window.removeEventListener("focus", scheduleForegroundCheck);
      document.removeEventListener(
        "visibilitychange",
        checkConnectionOnVisibilityChange,
      );
    };
  }, [completeIfConnected, isOpen, verificationData]);

  return (
    <ChannelModalShell
      isOpen={isOpen}
      dialogOnClose={() => {}}
      onClose={handleClose}
      iconWrapperClass="bg-green-100"
      icon={
        <svg
          className="h-6 w-6 text-green-600"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.594z" />
        </svg>
      }
      title="Add Your Assistant on WhatsApp"
      loading={loading}
      loadingLabel="Generating verification code..."
      spinnerColorClass="border-green-600"
      error={error}
      errorAction={
        <button
          onClick={handleRegenerateCode}
          className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
        >
          Try again
        </button>
      }
    >
      {verificationData && !loading && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-4">
            Open WhatsApp and send the message that's ready for you. We only see messages you send your assistant.
          </p>

          <a
            href={verificationData.whatsapp_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOpenWhatsApp}
            className="block w-full bg-green-600 text-white px-4 py-2 rounded-md text-center font-medium hover:bg-green-700 transition-colors"
          >
            Open WhatsApp
          </a>

          {linkStage === "tried" && (
            <button
              type="button"
              onClick={handleShowManualOptions}
              className="mt-3 mx-auto block text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Didn't work?
            </button>
          )}

          {linkStage === "manual" && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-3">
                No problem. Send this message to your assistant on WhatsApp:
              </p>

              <dl className="bg-gray-50 border border-gray-200 rounded-md divide-y divide-gray-200 mb-3">
                {verificationData.whatsapp_number && (
                  <CopyableRow
                    label="WhatsApp number"
                    value={verificationData.whatsapp_number}
                    copyLabel="Copy number"
                    copied={copied === "number"}
                    onCopy={(number) => void copyToClipboard(number, "number")}
                  />
                )}
                <CopyableRow
                  label="Message"
                  value={`VERIFY ${verificationData.verification_code}`}
                  copyLabel="Copy message"
                  copied={copied === "code"}
                  onCopy={(message) => void copyToClipboard(message, "code")}
                />
              </dl>

              <button
                type="button"
                onClick={() =>
                  void copyToClipboard(verificationData.whatsapp_link, "link")
                }
                className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                {copied === "link" ? "Link copied" : "Copy link"}
              </button>
            </div>
          )}

          {timeLeft > 0 ? (
            <p className="mt-4 text-center text-xs text-gray-500">
              Expires in <strong>{countdownFormatted}</strong>. We'll detect the connection automatically.
            </p>
          ) : (
            <p className="mt-4 text-center text-xs text-red-600">
              Code expired.{" "}
              <button
                onClick={handleRegenerateCode}
                className="text-red-600 hover:text-red-500 underline font-medium"
              >
                Generate a new one
              </button>
            </p>
          )}
        </div>
      )}
    </ChannelModalShell>
  );
}
